"use client";

import { useId, useState } from "react";
import { ChevronDownIcon, HelpCircleIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import type { PackageSummary } from "./pricing";

export function OrderSummaryCard({
  summary,
  className,
}: {
  summary: PackageSummary;
  className?: string;
}) {
  const { lines, dueToday } = summary;
  const [open, setOpen] = useState(false);
  const detailsId = useId();

  // Below lg the card sits at the end of the page and starts collapsed, showing
  // just the total; from lg up it's the sticky sidebar and always expanded, so
  // every collapsed-state class is paired with an `lg:` override.
  const collapsible = open ? "flex" : "hidden";

  return (
    <aside
      className={cn(
        "flex w-full flex-col items-start gap-6 rounded-3xl bg-[#0E1116] px-4 pb-4 pt-4 outline outline-1 -outline-offset-1 outline-[#30363D] sm:px-5 lg:gap-6 lg:pb-5 lg:pt-10",
        // Follows the customer down the long steps. The offsets clear the site
        // nav (4rem, 5rem at xl) plus StepShell's ~70px sticky header; the
        // max-height lets the card scroll internally rather than overflow the
        // viewport on the taller steps. Sticky needs the row to be
        // `items-start`, which the step layouts already set.
        "lg:sticky lg:top-[134px] lg:max-h-[calc(100vh-154px)] lg:overflow-y-auto xl:top-[150px] xl:max-h-[calc(100vh-170px)]",
        className,
      )}
    >
      <span
        className={cn(
          "items-center gap-2 rounded-[100px] bg-[#1F293D] px-5 py-[10px] outline outline-1 -outline-offset-1 outline-[#30363D] lg:inline-flex",
          collapsible,
        )}
      >
        <HelpCircleIcon className="h-4 w-4 shrink-0 text-[#58A6FF]" />
        <span className="font-inter text-sm font-semibold text-[#58A6FF]">Need Guidance?</span>
      </span>

      <div className="w-full overflow-hidden rounded-xl bg-[#161B22] outline outline-1 -outline-offset-1 outline-[#30363D]">
        <h2
          className={cn(
            "border-b border-[#30363D] bg-[#21262D] px-5 py-4 font-inter text-base font-bold text-[#F0F6FC] lg:block",
            open ? "block" : "hidden",
          )}
        >
          Your Mutetaxes Package
        </h2>

        <dl id={detailsId} className={cn("flex-col lg:flex", collapsible)}>
          {lines.map((line) => (
            <div
              key={line.label}
              className="flex items-center justify-between gap-4 border-b border-[#30363D] px-5 py-4"
            >
              <dt
                className={cn(
                  "max-w-[200px] font-inter text-sm font-medium leading-[19.6px]",
                  line.credit ? "italic text-[#39D353]" : "text-[#F0F6FC]",
                )}
              >
                {line.label}
              </dt>
              <dd className="flex flex-col items-end gap-0.5">
                <span
                  className={cn(
                    "font-inter text-[15px] font-bold",
                    line.credit ? "text-[#39D353]" : "text-[#F0F6FC]",
                  )}
                >
                  {line.amount}
                </span>
                <span className="font-inter text-xs text-[#8B949E]">{line.period}</span>
              </dd>
            </div>
          ))}
        </dl>

        {/* Two totals rather than one, so each breakpoint gets correct
            semantics: a real button that owns the disclosure on mobile, and a
            plain row on desktop where there is nothing to toggle. A single
            button hidden with `lg:hidden` would still be focusable and would
            announce a collapsed state that doesn't exist there. */}
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          aria-expanded={open}
          aria-controls={detailsId}
          className="flex w-full items-center justify-between gap-4 bg-[#21262D] p-4 text-left sm:p-5 lg:hidden"
        >
          <span className="font-inter text-base font-bold text-[#F0F6FC]">Due today</span>
          <span className="flex items-center gap-2">
            <span className="font-inter text-lg font-extrabold text-[#58A6FF]">{dueToday}</span>
            <ChevronDownIcon
              aria-hidden
              className={cn(
                "h-5 w-5 shrink-0 text-[#8B949E] transition-transform",
                open && "rotate-180",
              )}
            />
          </span>
          <span className="sr-only">{open ? "Hide package details" : "Show package details"}</span>
        </button>

        <div className="hidden items-center justify-between gap-4 bg-[#21262D] p-5 lg:flex">
          <span className="font-inter text-base font-bold text-[#F0F6FC]">Due today</span>
          <span className="font-inter text-lg font-extrabold text-[#58A6FF]">{dueToday}</span>
        </div>
      </div>
    </aside>
  );
}
